import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Routes, Route, Navigate, Link, useLocation, useNavigate } from 'react-router-dom';
import PrimesIndemnitesPage from './PrimesIndemnitesPage';
import AvancesPretPage from './AvancesPretPage';
import IncidentsPrejudicesPage from './IncidentsPrejudicesPage';
import MissionsDeplacementsPage from './MissionsDeplacementsPage';
import HeuresSupplementairesPage from './HeuresSupplementairesPage';
import JournalCaissePage from './JournalCaissePage';
import { motion } from 'framer-motion';
import {
  calculateSaudiPayrollSummary,
  mapSalariesConfigToGosiSettings,
} from './utils/payrollSaudiFormat';
import BulletinSalaire from './components/salaries/BulletinSalaire';
import GOSICalculator from './components/salaries/GOSICalculator';
import { useSalairesConfig } from './useSalairesConfig';
import { useSalairesEmployeeOptions } from './salairesListPageUtils';
import BaremesManager from './components/salaries/BaremesManager';
import RappelsCalculator from './components/salaries/RappelsCalculator';
import CongesCalculator from './components/salaries/CongesCalculator';
import PrimesIndemnitesManager from './components/salaries/PrimesIndemnitesManager';
import FormulaEditorCalcul from './components/salaries/FormulaEditorCalcul';
import DeclarationsManager from './components/salaries/DeclarationsManager';
import CalculSalaires from './CalculSalairesSaudi';


const RecapSalaire = () => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.salaires.pages.salariesPage.${key}`, opts);
  return (
  <div className="p-6">
    <h1 className="text-2xl font-semibold mb-6">{p('recap_title')}</h1>
    <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 shadow-xl border border-white/10">
      <p className="text-lg">{p('recap_journal')}</p>
      {/* Contenu à implémenter */}
    </div>
  </div>
);
};

const FichePaie = () => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.salaires.pages.salariesPage.${key}`, opts);
  return (
  <div className="p-6">
    <h1 className="text-2xl font-semibold mb-6">{p('payslip_title')}</h1>
    <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 shadow-xl border border-white/10">
      <p className="text-lg">{p('payslip_manage')}</p>
      {/* Contenu à implémenter */}
    </div>
  </div>
);
};

const ParametresSalaire = () => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.salaires.pages.salariesPage.${key}`, opts);
  const salariesConfig = useSalairesConfig();
  const [currentParamTab, setCurrentParamTab] = useState('primesIndemnites');
  
  // Options de menu pour les paramètres
  const paramMenuOptions = [
    { id: 'primesIndemnites', label: p('param_bonuses'), icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
    { id: 'formulesCalcul', label: p('param_formulas'), icon: 'M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z' },
    { id: 'gosi', label: p('param_gosi'), icon: 'M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z' },
    { id: 'baremesGrille', label: p('param_scales'), icon: 'M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z' },
    { id: 'rappels', label: p('param_recalls'), icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' },
    { id: 'conges', label: p('param_leaves'), icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
    { id: 'declarations', label: p('param_declarations'), icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
  ];


  // Rendu du contenu en fonction de l'onglet actif
  const renderContent = () => {
    switch(currentParamTab) {
      case 'primesIndemnites':
        return <PrimesIndemnitesManager />;
      case 'formulesCalcul':
        return <FormulaEditorCalcul />;
      case 'gosi':
        return <GOSICalculator settings={salariesConfig} />;
      case 'baremesGrille':
        return <BaremesManager />;
      case 'rappels':
        return <RappelsCalculator />;
      case 'conges':
        return <CongesCalculator />;
      case 'declarations':
        return <DeclarationsManager />;
      default:
        return <div className="text-white">{p('param_select')}</div>;
    }
  };
  
  return (
    <div className="p-4">
      <div className="backdrop-blur-sm border-b border-teal-700 p-4 mb-6">
        <h1 className="text-xl font-bold text-white">{p('param_calc_title')}</h1>
        <p className="text-white/80 text-sm">{p('param_calc_subtitle')}</p>
      </div>
      
      {/* Barre d'onglets horizontale */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-teal-700/30 mb-6">
        <div className="flex space-x-1 overflow-x-auto hide-scrollbar">
          {paramMenuOptions.map(option => {
            const active = currentParamTab === option.id;
            return (
              <button
                key={option.id}
                onClick={() => setCurrentParamTab(option.id)}
                className={`relative flex items-center justify-center px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                  active ? 'text-white' : 'text-white/70 hover:bg-teal-800/50 hover:text-white'
                }`}
              >
                {active && (
                  <motion.div
                    layoutId="param-bubble"
                    className="absolute inset-0 z-0 bg-gradient-to-r from-teal-600 to-emerald-600 rounded-2xl"
                    transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                  />
                )}
                <span className="relative z-10 flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={option.icon} />
                  </svg>
                  {option.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Contenu principal */}
      <div className="backdrop-blur-sm border border-teal-700/30 rounded-lg p-4">
        {renderContent()}
      </div>
    </div>
  );
};

const BulletinPaiePage = () => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.salaires.pages.salariesPage.${key}`, opts);
  const salariesConfig = useSalairesConfig();
  const gosiSettings = useMemo(
    () => mapSalariesConfigToGosiSettings(salariesConfig),
    [salariesConfig],
  );
  const { employees: hrEmployees, loading: employeesLoading } = useSalairesEmployeeOptions();
  const employees = useMemo(
    () => hrEmployees.map((emp) => ({
      ...emp,
      mat: emp.mat || emp.employeeId || String(emp.id),
      nom: emp.nom || emp.lastName || '',
      prenom: emp.prenom || emp.firstName || '',
      salaireBase: emp.salaireBase ?? emp.salary ?? emp.basic_salary ?? 0,
    })),
    [hrEmployees],
  );

  const payrollYear = new Date().getFullYear();
  const bulletinPeriodChoices = useMemo(
    () => Array.from({ length: 12 }, (_, i) => ({
      value: `${payrollYear}-${String(i + 1).padStart(2, '0')}`,
      monthKey: [
        'month_january', 'month_february', 'month_march', 'month_april',
        'month_may', 'month_june', 'month_july', 'month_august',
        'month_september', 'month_october', 'month_november', 'month_december',
      ][i],
      year: payrollYear,
      month: i + 1,
    })),
    [payrollYear],
  );

  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [periode, setPeriode] = useState(`${payrollYear}-${String(new Date().getMonth() + 1).padStart(2, '0')}`);
  const [showBulletin, setShowBulletin] = useState(false);
  const selectedPeriod = bulletinPeriodChoices.find((c) => c.value === periode) || bulletinPeriodChoices[0];

  useEffect(() => {
    if (employees.length && !selectedEmployeeId) {
      setSelectedEmployeeId(String(employees[0].id));
    }
  }, [employees, selectedEmployeeId]);

  const selectedEmployee = useMemo(
    () => employees.find((e) => String(e.id) === String(selectedEmployeeId)) || null,
    [employees, selectedEmployeeId],
  );

  const bulletinPayload = useMemo(() => {
    if (!selectedEmployee) return null;

    const monthNum = selectedPeriod.month;
    const yearStr = String(selectedPeriod.year);
    const firstDay = `01/${String(monthNum).padStart(2, '0')}/${yearStr}`;
    const lastDay = new Date(parseInt(yearStr, 10), monthNum, 0).getDate();
    const lastDayStr = `${lastDay}/${String(monthNum).padStart(2, '0')}/${yearStr}`;
    const workingDays = salariesConfig.monthlyWorkingDays || 30;

    const summary = calculateSaudiPayrollSummary(selectedEmployee, null, {
      ...gosiSettings,
      housingPct: salariesConfig.housingAllowancePct,
    });
    const { allowances, gosi, saned } = summary;

    const employeeData = {
      matricule: selectedEmployee.mat || '',
      nom: selectedEmployee.nom || '',
      prenom: selectedEmployee.prenom || '',
      dateNaissance: selectedEmployee.dateNaissance || selectedEmployee.date_of_birth || '',
      adresse: selectedEmployee.adresse || selectedEmployee.address || '',
      fonction: selectedEmployee.fonction || selectedEmployee.poste || selectedEmployee.position || '',
      dateEntree: selectedEmployee.dateRecrutement || selectedEmployee.hireDate || '',
      numeroSS: selectedEmployee.gosiNumber || selectedEmployee.securiteSociale || '',
      numeroCCP: selectedEmployee.iban || selectedEmployee.numCcp || '',
      situationFamiliale: selectedEmployee.situationFamiliale || p('demo_marital_married'),
      enfants: selectedEmployee.enfants ?? selectedEmployee.nombreEnfants ?? 0,
      grille: selectedEmployee.grille || p('na'),
    };

    const salaryData = {
      periode: {
        du: firstDay,
        au: lastDayStr,
        joursOuvrables: workingDays,
        joursTravailles: workingDays,
        joursAbsences: 0,
        heuresTravaillees: workingDays * 8,
      },
      gains: [
        { code: 'SB', libelle: p('demo_base_salary'), base: '', taux: '', nombreOuMontant: summary.salaireBase, imposable: true, cotisable: true },
        { code: 'IH', libelle: p('demo_housing_allowance'), base: summary.salaireBase, taux: salariesConfig.housingAllowancePct, nombreOuMontant: allowances.housing, imposable: false, cotisable: false },
        { code: 'IT', libelle: p('demo_transport_allowance'), base: '', taux: '', nombreOuMontant: allowances.transport, imposable: false, cotisable: false },
        { code: 'IM', libelle: p('demo_mobile_allowance'), base: '', taux: '', nombreOuMontant: allowances.mobile, imposable: false, cotisable: false },
      ],
      retenues: [
        { code: 'GOSI', libelle: p('demo_gosi_employee'), base: summary.salaireBase, taux: gosi?.employeeRate ?? salariesConfig.gosiEmployeeRate, nombreOuMontant: summary.retenueGOSISalarie, imposable: false, cotisable: false },
        { code: 'SANED', libelle: p('demo_saned_employee'), base: summary.salaireBase, taux: saned?.employeeRate ?? salariesConfig.sanedEmployeeRate, nombreOuMontant: summary.retenueSANEDSalarie, imposable: false, cotisable: false },
      ],
      cotisationsEmployeur: [
        { libelle: p('demo_employer_gosi'), taux: gosi?.employerRate ?? salariesConfig.gosiEmployerRate, montant: summary.retenueGOSIEmployeur },
        { libelle: p('demo_employer_saned'), taux: saned?.employerRate ?? salariesConfig.sanedEmployerRate, montant: summary.retenueSANEDEmployeur },
        { libelle: p('demo_employer_eosb'), taux: 0, montant: summary.provisionEOSB || 0 },
      ],
      totaux: {
        brut: summary.salaireBrut,
        cotisable: summary.contributoryBase,
        imposable: summary.salaireImposable,
        retenues: (summary.retenueGOSISalarie || 0) + (summary.retenueSANEDSalarie || 0),
        net: summary.netAPayer,
      },
    };

    return { employeeData, salaryData };
  }, [selectedEmployee, selectedPeriod, gosiSettings, salariesConfig, p]);

  const handleSelectEmployee = (e) => {
    setSelectedEmployeeId(e.target.value);
    setShowBulletin(false);
  };

  const handleGenererBulletin = () => {
    if (!selectedEmployee) return;
    setShowBulletin(true);
  };

  const handlePrintBulletin = () => {
    window.print();
  };

  if (employeesLoading) {
    return (
      <div className="backdrop-blur-sm p-6 shadow-lg">
        <p className="text-white/70">{p('loading_data')}</p>
      </div>
    );
  }

  return (
    <div className="backdrop-blur-sm p-6 shadow-lg">
      <h2 className="text-xl font-bold text-white mb-6">{p('bulletin_gen_title')}</h2>

      {employees.length === 0 ? (
        <div className="bg-white/5 border border-white/10 rounded-xl p-8 text-center">
          <p className="text-white/60">{p('no_employees', { defaultValue: 'لا يوجد موظفون نشطون — أضف موظفين من الموارد البشرية.' })}</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">{p('bulletin_select_employee')}</label>
              <select
                className="w-full bg-white/5 border border-teal-700 rounded-lg px-3 py-2 text-white"
                value={selectedEmployeeId}
                onChange={handleSelectEmployee}
              >
                {employees.map((emp) => (
                  <option key={emp.id} value={String(emp.id)}>
                    {emp.prenom} {emp.nom} ({emp.mat})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">{p('period')}</label>
              <select
                className="w-full bg-white/5 border border-teal-700 rounded-lg px-3 py-2 text-white"
                value={periode}
                onChange={(e) => { setPeriode(e.target.value); setShowBulletin(false); }}
              >
                {bulletinPeriodChoices.map((opt) => (
                  <option key={opt.value} value={opt.value}>{p(opt.monthKey)} {opt.year}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <button
                type="button"
                onClick={handleGenererBulletin}
                disabled={!selectedEmployee}
                className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg disabled:opacity-50"
              >
                {p('bulletin_generate')}
              </button>
            </div>
          </div>

          {showBulletin && bulletinPayload && (
            <div className="mt-8">
              <div className="flex justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">{p('bulletin_preview')}</h3>
                <button
                  type="button"
                  onClick={handlePrintBulletin}
                  className="px-3 py-1 bg-teal-700 hover:bg-teal-800 text-white rounded flex items-center"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                  </svg>
                  {p('bulletin_print')}
                </button>
              </div>

              <div className="bg-white p-8 rounded-lg">
                <BulletinSalaire
                  employeeData={bulletinPayload.employeeData}
                  salaryData={bulletinPayload.salaryData}
                  month={p(selectedPeriod.monthKey)}
                  year={String(selectedPeriod.year)}
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const SalariesPage = ({ defaultView = 'stats' }) => {
  // Render the appropriate component based on defaultView prop
  const renderContent = () => {
    switch (defaultView) {
      case 'bulletins':
        return <BulletinPaiePage />;
      case 'stats':
      default:
        return <CalculSalaires />;
    }
  };

  return (
    <div className="flex-1 flex flex-col">
      {/* Contenu principal - Navigation handled by sidebar */}
      <div className="flex-1 p-4 overflow-auto">
        {renderContent()}
      </div>
    </div>
  );
};

export default SalariesPage; 


